import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUser, FiMail, FiEye, FiEyeOff, FiZap, FiShield, FiArrowRight, FiTarget, FiX, FiBookOpen, FiBarChart3 } = FiIcons;

const LoginForm = ({ onLogin, onClose }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email.trim() || !formData.password.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (isSignUp && !formData.name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Normalize email for consistent storage
      const normalizedEmail = formData.email.toLowerCase().trim();
      
      // Check if this is a returning user
      const existingUserKey = `dreamforge_habits_${normalizedEmail.replace(/[^a-zA-Z0-9]/g, '_')}`;
      const hasExistingData = localStorage.getItem(existingUserKey) !== null;
      
      const userData = {
        id: normalizedEmail.replace(/[^a-zA-Z0-9]/g, '_'),
        name: isSignUp ? formData.name.trim() : (formData.name.trim() || formData.email.split('@')[0]),
        email: normalizedEmail,
        isNewUser: isSignUp && !hasExistingData, // Only new if signing up AND no existing data
        createdAt: new Date().toISOString()
      };
      
      console.log('Login successful for user:', userData.email, 'isNewUser:', userData.isNewUser);
      
      // Call the onLogin function passed from parent
      if (onLogin) {
        onLogin(userData);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm rounded-3xl shadow-dominance max-w-md w-full overflow-hidden border border-forge-400/30"
      >
        {/* Header */}
        <div className="bg-forge-gradient p-6 text-shadow-950 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-shadow-950 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiTarget} className="text-forge-400 text-lg" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">DreamForge</h1>
                  <p className="text-shadow-950/70 text-sm">Forge Your Future</p>
                </div>
              </div>
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-shadow-900/20 rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiX} />
                </button>
              )}
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">
                {isSignUp ? 'Begin Your Journey' : 'Welcome Back, Warrior'}
              </h2>
              <p className="text-shadow-950/80">
                {isSignUp ? 'Create your account to start dominating your habits' : 'Continue your path to greatness'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/20 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm"
            >
              {error}
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={isSignUp ? 'signup' : 'login'}
              initial={{ opacity: 0, x: isSignUp ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isSignUp ? -20 : 20 }}
              className="space-y-4"
            >
              {isSignUp && (
                <div>
                  <label className="block text-sm font-medium text-forge-300 mb-2">
                    <SafeIcon icon={FiUser} className="mr-2 inline" />
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200 placeholder-forge-300/50"
                    placeholder="Enter your name"
                    required={isSignUp}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-forge-300 mb-2">
                  <SafeIcon icon={FiMail} className="mr-2 inline" />
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200 placeholder-forge-300/50"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-forge-300 mb-2">
                  <SafeIcon icon={FiShield} className="mr-2 inline" />
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200 placeholder-forge-300/50"
                    placeholder="Enter your password (min 6 chars)"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-forge-400 hover:text-forge-200 transition-colors"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-forge-gradient text-shadow-950 py-3 px-6 rounded-2xl font-bold hover:shadow-power transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-shadow-950 border-t-transparent rounded-full animate-spin"></div>
                <span>{isSignUp ? 'Creating Account...' : 'Signing In...'}</span>
              </>
            ) : (
              <>
                <span>{isSignUp ? 'Begin Journey' : 'Enter the Forge'}</span>
                <SafeIcon icon={FiArrowRight} />
              </>
            )}
          </button>

          {/* Toggle Mode */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-forge-400 hover:text-forge-200 transition-colors font-medium"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
            </button>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-forge-400/20">
            <div className="text-center">
              <div className="w-8 h-8 bg-gradient-to-r from-emerald-400 to-green-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiZap} className="text-white text-sm" />
              </div>
              <p className="text-xs text-forge-300">
                Habit<br />Tracking
              </p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiTarget} className="text-white text-sm" />
              </div>
              <p className="text-xs text-forge-300">
                Vision<br />Board
              </p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-gradient-to-r from-pink-400 to-rose-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiBookOpen} className="text-white text-sm" />
              </div>
              <p className="text-xs text-forge-300">
                Mind<br />Reflections
              </p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiBarChart3} className="text-white text-sm" />
              </div>
              <p className="text-xs text-forge-300">
                Analytics<br />Insights
              </p>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default LoginForm;